import { createSlice } from '@reduxjs/toolkit'
import { API } from 'src/utils/interceptor'
import {
  isParLevelLimitError,
  PAR_LEVEL_LIMIT_ERROR_CODE,
} from 'src/utils/parLevelPaywall'
import { getVenueProductSaveErrorMessage } from 'src/utils/growthEngineCatalogueQuickAdd'
import {
  RejectedNotification,
  SuccessNotification,
} from 'src/notifications/SuccessNotification'
import Sentry from 'src/utils/sentry'

const initialState = { products: [], isLoading: false }

const venueProductsSlice = createSlice({
  name: 'venueProducts',
  initialState,
  reducers: {
    setProducts: (state, action) => {
      state.products = action.payload
    },
    addProduct: (state, action) => {
      state.products.push(action.payload)
    },
    deleteProduct: (state, action) => {
      state.products = state.products.filter(
        item => item._id !== action.payload,
      )
    },
    editProduct: (state, action) => {
      const index = state.products.findIndex(
        product => product._id === action.payload._id,
      )
      if (index !== -1) state.products[index] = action.payload
    },
    editProductProduct: (state, action) => {
      // As in the "product" within the venue product
      const newState = state.products.map(item => {
        if (item.product._id === action.payload._id) {
          item.product = action.payload
        }
        return item
      })
      state.products = newState
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload
    },
    resetVenueProducts: state => {
      state = initialState
      return state
    },
  },
})

export const {
  setProducts,
  addProduct,
  deleteProduct,
  editProduct,
  editProductProduct,
  setIsLoading,
  resetVenueProducts,
} = venueProductsSlice.actions

export const fetchVenueProducts = venueId => async dispatch => {
  try {
    dispatch(setIsLoading(true))
    const data = await API.get('api', `venue/${venueId}/products`)
    dispatch(setProducts(data))
    dispatch(setIsLoading(false))
  } catch (e) {
    dispatch(setIsLoading(false))
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const addVenueProduct =
  (data, options = {}) =>
  async dispatch => {
    try {
      const venueProduct = await API.post(
        'api',
        `venue/${data.venue}/product`,
        {
          body: data,
        },
      )
      dispatch(addProduct(venueProduct))
      if (!options.silent) {
        SuccessNotification({
          message: 'Added to products',
          description: `"${data.title}" has been added to your products and is now available to order via the app`,
        })
      }
      return venueProduct
    } catch (e) {
      if (isParLevelLimitError(e)) {
        return PAR_LEVEL_LIMIT_ERROR_CODE
      }
      console.error(e)
      Sentry.captureException(e)
      if (!options.silent) {
        RejectedNotification({
          message: 'Could not add product',
          description: getVenueProductSaveErrorMessage(e),
        })
      }
      return false
    }
  }

export const editVenueProduct =
  (data, options = {}) =>
  async dispatch => {
    const { notifyFavourite = false } = options
    try {
      if (!data?.isSpecialProduct) {
        delete data?.specialPrice
        delete data?.OMSSpecialPrice
      }

      if (!data.secondaryZone) {
        data.secondaryZone = ''
      }
      const _product = await API.post('api', `venue/${data.venue}/product`, {
        body: data,
      })
      dispatch(editProduct(_product))
      if (notifyFavourite) {
        SuccessNotification({
          message: _product?.isFavourite
            ? 'Added to favourites'
            : 'Removed from favourites',
          description: `"${
            _product?.title || data?.title || 'Product'
          }" was updated`,
        })
      }
      return _product
    } catch (e) {
      if (isParLevelLimitError(e)) {
        return PAR_LEVEL_LIMIT_ERROR_CODE
      }
      console.error(e)
      Sentry.captureException(e)
      if (notifyFavourite) {
        RejectedNotification({
          message: 'Could not update favourite',
          description: getVenueProductSaveErrorMessage(e),
        })
      }
      return false
    }
  }

export const deleteVenueProduct = (venueProduct, venueId) => async dispatch => {
  try {
    await API.del('api', `venue/${venueId}/product/${venueProduct._id}`)
    dispatch(deleteProduct(venueProduct._id))
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const deleteAllVenueProducts = venueId => async dispatch => {
  try {
    await API.del('api', `venue/${venueId}/products`)
    dispatch(setProducts([]))
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const uploadVenueProductsCSV =
  (data, venueId, setCSVLoading) => async dispatch => {
    try {
      const writeProducts = await API.post('api', `admin/venueProducts-csv`, {
        body: { items: data, venueId },
      })

      let errorMessage = []
      let existsMessage = []

      if (writeProducts.error && writeProducts.error.length > 0) {
        errorMessage = writeProducts.error
          .map(errored => {
            if (errored.product) {
              return `Product ID: ${errored.product}; zone: ${errored.zone}; defaultQuantity: ${errored.defaultQuantity}`
            }
            return null
          })
          .filter(msg => msg)
      }

      if (writeProducts.exists && writeProducts.exists.length > 0) {
        existsMessage = writeProducts.exists
          .map(existing => {
            if (existing.product) {
              return `Product ID: ${existing.product}; zone: ${existing.zone}; defaultQuantity: ${existing.defaultQuantity}`
            }
            return null
          })
          .filter(msg => msg)
      }

      if (errorMessage.length || existsMessage.length) {
        const formattedErrMessage = errorMessage.toString().replace(',', '\n')
        const formattedExstMessage = existsMessage.toString().replace(',', '\n')
        RejectedNotification({
          message: 'Error uploading products',
          description: `${
            formattedErrMessage &&
            `The following product(s) could not be uploaded:
           ${formattedErrMessage}`
          }
             ${
               formattedExstMessage &&
               `The following product(s) already exist and have been skipped: 
              ${formattedExstMessage}`
             }`,
        })
        SuccessNotification({
          message: 'Shopping list upload finished',
          description:
            'Some rows were updated. Review any skipped products in the notification above.',
        })
      } else {
        SuccessNotification({
          message: 'Shopping list updated',
          description: 'Your CSV upload completed successfully.',
        })
      }

      await dispatch(fetchVenueProducts(venueId))
    } catch (error) {
      console.error(error)
      Sentry.captureException(error)
      RejectedNotification({
        message: 'Error uploading CSV',
        description:
          'There was an error uploading the CSV file. Please try again.',
      })
      return false
    } finally {
      setCSVLoading(false)
    }
  }

export default venueProductsSlice
